import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Guid } from "@/Common/Models/Guid";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { PrescriptionRenewalRequest } from "../Api/Dto/PrescriptionRenewalRequest";
import { fetchPrescriptionsRenewals } from "../Api/PrescriptionRenewalsClient";
import { PrescriptionRenewal } from "../Models/PrescriptionRenewal";
import { PrescriptionRenewalFilterSettingModel } from "../Models/PrescriptionRenewalFilterSettingModel";
import { PrescriptionRenewalInboxSettings } from "../Models/PrescriptionRenewalInboxSettings";
import { PrescriptionRenewalModel } from "../Models/PrescriptionRenewalModel";

interface IUsePrescriptionRenewalPaginationReturn {
    onPreviousPrescriptionRenewal: () => Promise<PrescriptionRenewalRequest>;
    onNextPrescriptionRenewal: () => Promise<PrescriptionRenewalRequest>;
    getCurrentPrescriptionRenewalIndexFromTotal: () => number;
    loadPrescriptionRenewalsInfo: (updatedPrescriptionRenewalRequestData?: PrescriptionRenewalRequest) => Promise<PrescriptionRenewal[]>;
    prescriptionRenewalsCount: number;
    prescriptionRenewalRequestData: PrescriptionRenewalRequest;
    isLastPrescriptionRenewal: boolean;
    isPreviousDisabled: boolean;
}

const UsePrescriptionRenewalPagination = (prescriptionRenewalId: Guid | string, prescriptionRenewalInboxSettings: PrescriptionRenewalInboxSettings): IUsePrescriptionRenewalPaginationReturn => {
    const navigate = useNavigate();
    const [prescriptionRenewalRequestData, setPrescriptionRenewalRequestData] = useState<PrescriptionRenewalRequest>({
        ResponsibleProviderContactId: prescriptionRenewalInboxSettings.responsibleProviderContactId,
        QueryOrderType: prescriptionRenewalInboxSettings.queryOrderType,
        QueryOrderDirection: prescriptionRenewalInboxSettings.queryOrderDirection,
        SearchText: prescriptionRenewalInboxSettings.searchText,
        QueryCurrentPageNumber: prescriptionRenewalInboxSettings.currentPageNumber,
        PrescriptionRenewalFilterSettings: cacheStore.UserSettings.PrescriptionRenewalFilterSettings ?? PrescriptionRenewalFilterSettingModel.toCacheModel(new PrescriptionRenewalFilterSettingModel())
    });
    const [prescriptionRenewalInfo, setPrescriptionRenewalInfo] = useState<PrescriptionRenewalModel>(null);
    const navigateToInboxIfIdIsNotFound = useCallback((prescriptionRenewals: PrescriptionRenewal[]) => {
        if (prescriptionRenewals?.any(prescriptionRenewal => prescriptionRenewal.id === prescriptionRenewalId)) {
            return;
        }

        navigate("/prescriptionrenewals");
    }, [prescriptionRenewalId]);

    const loadPrescriptionRenewalsInfo = useCallback(async (updatedPrescriptionRenewalRequestData?: PrescriptionRenewalRequest): Promise<PrescriptionRenewal[]> => {
        const responseData = await fetchPrescriptionsRenewals(updatedPrescriptionRenewalRequestData ?? prescriptionRenewalRequestData);
        const prescriptionRenewalData = PrescriptionRenewalModel.fromResponseModel(responseData);
        const pageCount: number = Math.floor((prescriptionRenewalData.prescriptionRenewalsCount / prescriptionRenewalRequestData?.PrescriptionRenewalFilterSettings.QueryPageSize) + 1);

        navigateToInboxIfIdIsNotFound(prescriptionRenewalData?.prescriptionRenewals);

        setPrescriptionRenewalInfo(prev => ({ ...prev, ...prescriptionRenewalData, totalPageCount: pageCount }));
        return prescriptionRenewalData.prescriptionRenewals;
    }, [prescriptionRenewalRequestData, navigateToInboxIfIdIsNotFound]);

    const onPreviousPrescriptionRenewal = async (): Promise<PrescriptionRenewalRequest> => {
        if (prescriptionRenewalRequestData.QueryCurrentPageNumber > 1 && getCurrentPrescriptionRenewalIndex() === 0) {
            const updatedPrescriptionRenewals = await navigateNextOrPreviousPage(prescriptionRenewalRequestData.QueryCurrentPageNumber - 1);

            navigate(`/prescriptionrenewals/${updatedPrescriptionRenewals[updatedPrescriptionRenewals.length - 1].id}/renewMedication`);
            return;
        }

        navigate(`/prescriptionrenewals/${getPrescriptionIdByIndex(getCurrentPrescriptionRenewalIndex() - 1)}/renewMedication`);
    };

    const onNextPrescriptionRenewal = async (): Promise<PrescriptionRenewalRequest> => {
        if (prescriptionRenewalRequestData.QueryCurrentPageNumber < prescriptionRenewalInfo?.totalPageCount && isPrescriptionRenewalLastIndex) {
            const updatedPrescriptionRenewals = await navigateNextOrPreviousPage(prescriptionRenewalRequestData.QueryCurrentPageNumber + 1);

            navigate(`/prescriptionrenewals/${updatedPrescriptionRenewals[0].id}/renewMedication`);
            return;
        }

        navigate(`/prescriptionrenewals/${getPrescriptionIdByIndex(getCurrentPrescriptionRenewalIndex() + 1)}/renewMedication`);
    };

    const navigateNextOrPreviousPage = async (pageNumber: number): Promise<PrescriptionRenewal[]> => {
        const updatedPrescriptionRenewalRequestData = new PrescriptionRenewalRequest({ ...prescriptionRenewalRequestData, QueryCurrentPageNumber: pageNumber });
        const prescriptionRenewaldata = await loadPrescriptionRenewalsInfo(updatedPrescriptionRenewalRequestData);
        const updateCurrentPageNumber: number = updatedPrescriptionRenewalRequestData.QueryCurrentPageNumber;
        const isCurrentPageNumberEqual: boolean = prescriptionRenewalInboxSettings.currentPageNumber === updateCurrentPageNumber;

        setPrescriptionRenewalRequestData(prev => ({ ...prev, QueryCurrentPageNumber: updateCurrentPageNumber }));

        if (!isCurrentPageNumberEqual) {
            requestToUpdateInboxSettingsCurrentPageNumber(updateCurrentPageNumber);
        }

        return prescriptionRenewaldata;
    };

    const requestToUpdateInboxSettingsCurrentPageNumber = (updatedPageNumber: number): void => {
        sessionStore.dispatch(
            LocalOperations.PrescriptionRenewal.SetPrescriptionRenewalInboxSettings,
            {
                prescriptionRenewalInboxSettings: { ...prescriptionRenewalInboxSettings, currentPageNumber: updatedPageNumber }
            }
        );
    };

    const getCurrentPrescriptionRenewalIndex = (): number => prescriptionRenewalInfo?.prescriptionRenewals.findIndex(prescriptionRenewal => prescriptionRenewal.id === prescriptionRenewalId) ?? 0;
    const getPrescriptionIdByIndex = (index: number): string => prescriptionRenewalInfo?.prescriptionRenewals[index].id.toString();
    const isPrescriptionRenewalLastIndex: boolean = prescriptionRenewalInfo?.prescriptionRenewals.length - 1 === getCurrentPrescriptionRenewalIndex();
    const isLastPrescriptionRenewal: boolean = prescriptionRenewalInfo?.totalPageCount === prescriptionRenewalRequestData?.QueryCurrentPageNumber && isPrescriptionRenewalLastIndex;
    const isPreviousDisabled: boolean = prescriptionRenewalRequestData?.QueryCurrentPageNumber === 1 && getCurrentPrescriptionRenewalIndex() === 0;
    const getCurrentPrescriptionRenewalIndexFromTotal = (): number => getCurrentPrescriptionRenewalIndex() + prescriptionRenewalRequestData?.PrescriptionRenewalFilterSettings.QueryPageSize * ((prescriptionRenewalRequestData?.QueryCurrentPageNumber ?? 1) - 1) + 1;
    const prescriptionRenewalsCount: number = prescriptionRenewalInfo?.prescriptionRenewalsCount;

    return {
        onPreviousPrescriptionRenewal,
        onNextPrescriptionRenewal,
        getCurrentPrescriptionRenewalIndexFromTotal,
        loadPrescriptionRenewalsInfo,
        prescriptionRenewalsCount,
        prescriptionRenewalRequestData,
        isLastPrescriptionRenewal,
        isPreviousDisabled
    };
};

export default UsePrescriptionRenewalPagination;
